import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  fetchPreferences,
  updatePreference,
} from '../../store/notificationsSlice';
import {
  requestPushPermission,
  getNotificationPermissionState,
  isPushSupported,
  NotificationPermissionState,
} from '../../utils/pushManager';
import { isIOS, isStandalone } from '../../utils/deviceDetect';
import { getPwaWorkspaceConfig } from '../../config/workspace-config';

interface NotificationCenterProps {
  onClose: () => void;
  onNavigateTab: (tab: 'home' | 'deposit' | 'chat' | 'profile') => void;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({ onClose, onNavigateTab }) => {
  const dispatch = useAppDispatch();
  const { items, preferences } = useAppSelector((state) => state.notifications);
  const { workspaceId } = useAppSelector((state) => state.auth);
  const [activeSubTab, setActiveSubTab] = useState<'inbox' | 'preferences'>('inbox');
  const [permissionState, setPermissionState] = useState<NotificationPermissionState>('default');
  const [requestingPush, setRequestingPush] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  const wsConfig = getPwaWorkspaceConfig();
  const appName = wsConfig.appName || 'Console';
  const isIosBrowser = isIOS() && !isStandalone();
  const pushSupported = isPushSupported();

  useEffect(() => {
    dispatch(fetchNotifications());
    dispatch(fetchPreferences());
    setPermissionState(getNotificationPermissionState());
  }, [dispatch]);

  const handleNotificationClick = (item: any) => {
    if (!item.read) {
      dispatch(markNotificationRead(item.id));
    }
    const type = item.type;
    const actionUrl = item.actionUrl;

    if (type === 'message' || actionUrl?.includes('/chat')) {
      onNavigateTab('chat');
      onClose();
    } else if (type === 'deposit' || actionUrl?.includes('deposit')) {
      onNavigateTab('deposit');
      onClose();
    }
  };

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsRead());
  };

  const handlePreferenceToggle = (category: string, channel: 'IN_APP' | 'PUSH', enabled: boolean) => {
    dispatch(updatePreference({ category, channel, enabled }));
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'message': return '💬';
      case 'action': return '⚡️';
      case 'alert': return '⚠️';
      default: return '⚙️';
    }
  };

  const getPreferenceValue = (category: string, channel: 'IN_APP' | 'PUSH'): boolean => {
    const pref = preferences.find((p) => p.category === category && p.channel === channel);
    return pref ? pref.enabled : true;
  };

  const handleEnableWebPush = async () => {
    setRequestingPush(true);
    setFeedbackMessage(null);

    const res = await requestPushPermission(workspaceId);
    setRequestingPush(false);
    setPermissionState(res.state);

    if (res.success) {
      setFeedbackMessage({ text: 'Web Push alerts enabled successfully!', type: 'success' });
    } else if (res.state === 'denied') {
      setFeedbackMessage({
        text: 'Notifications are blocked. Please enable notification permissions in your browser or device settings.',
        type: 'error',
      });
    } else if (res.message) {
      setFeedbackMessage({ text: res.message, type: 'info' });
    }
  };

  const unreadCount = items.filter((i) => !i.read).length;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(5, 8, 16, 0.85)',
        backdropFilter: 'blur(6px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          height: '80vh',
          backgroundColor: '#0F172A',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '18px 20px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <div style={{ fontSize: '18px', fontWeight: '900', color: '#fff', letterSpacing: '0.5px' }}>
            {appName} Alerts
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Sub-Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <button
            onClick={() => setActiveSubTab('inbox')}
            style={{
              flex: 1,
              padding: '12px',
              background: 'none',
              border: 'none',
              borderBottom: activeSubTab === 'inbox' ? '2px solid #8B5CF6' : 'none',
              color: activeSubTab === 'inbox' ? '#A78BFA' : 'rgba(255, 255, 255, 0.6)',
              fontWeight: '700',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Inbox {unreadCount > 0 && `(${unreadCount})`}
          </button>
          <button
            onClick={() => setActiveSubTab('preferences')}
            style={{
              flex: 1,
              padding: '12px',
              background: 'none',
              border: 'none',
              borderBottom: activeSubTab === 'preferences' ? '2px solid #8B5CF6' : 'none',
              color: activeSubTab === 'preferences' ? '#A78BFA' : 'rgba(255, 255, 255, 0.6)',
              fontWeight: '700',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Preferences
          </button>
        </div>

        {/* Push Notification Status Banner */}
        {pushSupported && (
          <div
            style={{
              padding: '12px 16px',
              background:
                permissionState === 'granted'
                  ? 'rgba(16, 185, 129, 0.08)'
                  : permissionState === 'denied'
                  ? 'rgba(239, 68, 68, 0.08)'
                  : 'rgba(139, 92, 246, 0.08)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            {isIosBrowser ? (
              <div style={{ fontSize: '11px', color: '#A78BFA', lineHeight: '1.4' }}>
                💡 <strong>iOS Push Note:</strong> Install {appName} to your Home Screen to enable instant push notifications.
              </div>
            ) : permissionState === 'granted' ? (
              <div style={{ fontSize: '12px', color: '#10B981', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>✓</span> Push notifications are active
              </div>
            ) : permissionState === 'denied' ? (
              <div style={{ fontSize: '11px', color: '#F87171', lineHeight: '1.4' }}>
                ⚠️ <strong>Notifications Blocked:</strong> To receive push alerts, enable notification permissions in your browser/device site settings.
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ fontSize: '12px', color: '#A78BFA', fontWeight: '600' }}>
                  Enable Push Notifications
                </div>
                <button
                  onClick={handleEnableWebPush}
                  disabled={requestingPush}
                  style={{
                    background: '#8B5CF6',
                    color: '#fff',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '800',
                    cursor: 'pointer',
                  }}
                >
                  {requestingPush ? 'ENABLING...' : 'ENABLE'}
                </button>
              </div>
            )}

            {feedbackMessage && (
              <div
                style={{
                  fontSize: '11px',
                  marginTop: '6px',
                  color: feedbackMessage.type === 'success' ? '#10B981' : feedbackMessage.type === 'error' ? '#F87171' : '#A78BFA',
                }}
              >
                {feedbackMessage.text}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        {activeSubTab === 'inbox' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {items.length > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  alignSelf: 'flex-end',
                  padding: '8px 16px',
                  background: 'none',
                  border: 'none',
                  color: '#A78BFA',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                ✓ Mark all as read
              </button>
            )}

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '0 16px 16px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleNotificationClick(item)}
                  style={{
                    backgroundColor: item.read ? 'rgba(255, 255, 255, 0.02)' : 'rgba(139, 92, 246, 0.08)',
                    border: `1px solid ${item.read ? 'rgba(255, 255, 255, 0.05)' : 'rgba(139, 92, 246, 0.3)'}`,
                    borderRadius: '14px',
                    padding: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{getIconForType(item.type)}</span>
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: item.read ? '600' : '800',
                          color: item.read ? '#E2E8F0' : '#FFFFFF',
                        }}
                      >
                        {item.title}
                      </span>
                    </div>
                    {!item.read && (
                      <div style={{ width: '8px', height: '8px', borderRadius: '4px', backgroundColor: '#8B5CF6' }} />
                    )}
                  </div>

                  <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.4', marginBottom: '6px' }}>
                    {item.message}
                  </div>

                  <div style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', textAlign: 'right' }}>
                    {item.created_at ? new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Now'}
                  </div>
                </div>
              ))}

              {items.length === 0 && (
                <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.4)', marginTop: '40px', fontSize: '13px' }}>
                  No notifications yet.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: '800', color: '#fff', marginBottom: '14px' }}>
              Notification Preferences
            </div>

            {/* Chat Messages */}
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: '14px',
                padding: '14px',
                marginBottom: '12px',
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#fff', marginBottom: '4px' }}>
                💬 Chat & Support Messages
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '10px' }}>
                Notifications for live support replies and concierge chats.
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>In-App Notifications</span>
                <input
                  type="checkbox"
                  checked={getPreferenceValue('chat', 'IN_APP')}
                  onChange={(e) => handlePreferenceToggle('chat', 'IN_APP', e.target.checked)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>Push Notifications</span>
                <input
                  type="checkbox"
                  checked={getPreferenceValue('chat', 'PUSH')}
                  onChange={(e) => handlePreferenceToggle('chat', 'PUSH', e.target.checked)}
                />
              </div>
            </div>

            {/* System Updates */}
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: '14px',
                padding: '14px',
                marginBottom: '12px',
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#fff', marginBottom: '4px' }}>
                ⚙️ System & Account Updates
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '10px' }}>
                Important announcements, transactions, and account security notifications.
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>In-App Notifications</span>
                <input
                  type="checkbox"
                  checked={getPreferenceValue('system', 'IN_APP')}
                  onChange={(e) => handlePreferenceToggle('system', 'IN_APP', e.target.checked)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>Push Notifications</span>
                <input
                  type="checkbox"
                  checked={getPreferenceValue('system', 'PUSH')}
                  onChange={(e) => handlePreferenceToggle('system', 'PUSH', e.target.checked)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
